/*
*  EQIT Authenticator
*
*	Copyright (C) 2022-2024, EQIT. All rights reserved.
*	Created by David Hsieh <david@eqit.com.tw>
*/
// css
import "./css/authenticator.css";
import { getClientHint, getParameters, getSessionId } from '../base/globals';
import { comObject, wsEQAPI } from '../base/eq';
import { authenticate } from './com/firebase';
class comAuthFirebase extends comObject {
    // The log level setting of this class.
    // It should be used only in development and remarked
    // in a release build.
    //
    // To enable the console message at runtime, issue the
    // following command in the browser's development console.
    //
    //	> EQ.logging.setLoggerLevel('TRACE', 'comAuthFirebase');
    //
    //logLevel: E_LOGLEVEL = E_LOGLEVEL.TRACE;
    get options() { return this._options || {}; }
    async go() {
        try {
            this.logger.debug(`${this.$className}.go() ...`, this.options);
            let result = await authenticate();
            await this._handleAuthResult(result);
        }
        catch (e) {
            this.logger.error('Unexpected Exception', e);
            alert('Unexpected Exception');
            location.reload();
        }
    }
    ////////////////////////////////////////////////////////
    async _handleAuthResult(authResult) {
        this.logger.debug(`${this.$className} authResult`, authResult);
        let o = this.options, providerdata = authResult.user.providerData[0], profile = authResult._tokenResponse, dat = {
            reqid: o.cpauth || null,
            providerId: profile.providerId,
            signInMethod: profile.providerId,
            profile: profile, // optional
            userid: '',
            username: '',
            givenname: '',
            surname: '',
            picture: '',
            email: ''
        };
        switch (profile.providerId) {
            case 'facebook.com':
                dat['userid'] = providerdata.uid;
                dat['username'] = profile.fullName;
                ;
                dat['givenname'] = profile.firstName;
                ;
                dat['surname'] = profile.lastName;
                dat['picture'] = profile.photoUrl;
                dat['email'] = profile.email;
                break;
            case 'google.com':
                dat['userid'] = providerdata.uid;
                dat['username'] = profile.fullName;
                dat['givenname'] = profile.firstName;
                dat['surname'] = profile.lastName;
                dat['picture'] = profile.photoUrl;
                dat['email'] = profile.email;
                dat['locale'] = profile.localId;
                break;
            default:
                alert(`${profile.providerId} is not supported yet!`);
                break;
        }
        if (o.cpauth) {
            let hint = await getClientHint();
            if (hint)
                dat['client_hint'] = hint;
            let redirect_url = await this._doCPAuth(dat);
            window.location.href = redirect_url;
        }
        else {
            let dn = await this._doSignOn(dat);
            if (dn) {
                let signed = await this._doSignIn(dn);
                this.logger.debug(`SignIn => ${signed}`);
            }
            window.location.href = "/download_center/product.html";
        }
    }
    _doCPAuth(dat) {
        let dfd = $.Deferred(), api = new wsEQAPI('authenticator/cpauth');
        this.logger.info('CPAUTH', dat);
        $.when(api.update(dat))
            .fail((r, e) => {
            this.logger.error(`CPAUTH fail: [${r}]`, e);
            alert('Internal service error, please contact the system administrator.');
            dfd.reject('Internal service error, please contact the system administrator.');
        })
            .done(d => {
            this.logger.info('CPAUTH success', d);
            let origin = d.payload.origin, reqid = d.payload.reqid;
            if (origin && reqid) {
                if (origin.indexOf('?') >= 0)
                    dfd.resolve(`${origin}&cpauth=${reqid}`);
                else
                    dfd.resolve(`${origin}?cpauth=${reqid}`);
            }
            else
                dfd.resolve('/');
        });
        return dfd.promise();
    }
    _doSignOn(dat) {
        let dfd = $.Deferred(), api = new wsEQAPI('authenticator/member');
        $.when(api.read(dat))
            .fail((r, e) => {
            this.logger.error(`[SIGNON]: internal error (1) - ${r}`, e);
            alert('Sign On service error (1), please contact the system adminstrator.');
            dfd.resolve(null);
        })
            .done(d => {
            if (!d.payload) {
                dat['sessid'] = getSessionId();
                $.when(api.create(dat))
                    .fail((r, e) => {
                    this.logger.error(`[SIGNON]: internal error (2) - ${r}`, e);
                    alert('Sign On service error (2), please contact the system adminstrator.');
                    dfd.resolve(null);
                })
                    .done(d => {
                    this.logger.info(`[SIGNON]: signed on`, d);
                    dfd.resolve(d.payload.dn);
                });
            }
            else {
                this.logger.info(`[SIGNON]: signed on already`, d);
                $.when(api.update(dat))
                    .always(() => {
                    dfd.resolve(d.payload.dn);
                });
            }
        });
        return dfd.promise();
    }
    _doSignIn(userdn) {
        let dfd = $.Deferred(), sid = getSessionId(), api = new wsEQAPI('authenticator/session');
        $.when(api.read(sid))
            .fail((r, e) => {
            this.logger.debug(`[${r}] =>`, e);
            $.when(api.create({ sessid: sid, dn: userdn }))
                .fail((r, e) => {
                this.logger.error(`[SIGNIN] internal error - ${r}`, e);
                dfd.resolve(false);
            })
                .done(d => {
                this.logger.info(`[SIGNIN] signed in - ${userdn}`, d);
                dfd.resolve(true);
            });
        })
            .done(d => {
            this.logger.info(`[SIGNIN] signed in already - ${userdn}`, d);
            $.when(api.update(sid)).always(() => { dfd.resolve(true); });
        });
        return dfd.promise();
    }
}
////////////////////////////////////////////////////////////
//
//  The main index page of Authenticator.
//
////////////////////////////////////////////////////////////
class mainPage {
    static get Instance() {
        if (!mainPage._instance)
            mainPage._instance = new mainPage();
        return mainPage._instance;
    }
    // Construction
    constructor() { }
    hide() { return true; }
    show() {
        this.doAuth();
        return true;
    }
    ////////////////////////////////////////////////////////
    validate(aid) {
        let dfd = $.Deferred(), api = new wsEQAPI('authenticator/cpauth');
        $.when(api.read(aid))
            .fail((r, e) => {
            console.error(`authenticator/cpauth [${aid}] => [${r}]`, e);
            dfd.resolve(false);
        })
            .done(d => {
            console.info(`authenticator/cpauth [${aid}] is valid`);
            dfd.resolve(true);
        });
        return dfd.promise();
    }
    async doAuth() {
        let aid = getParameters().aid, cpauth = false;
        if (aid)
            cpauth = await this.validate(aid);
        let opt = cpauth ? { cpauth: aid } : {}, firebase = new comAuthFirebase($('.firebaseui-auth-container'), opt);
        firebase.go();
    }
}
// The main entry for the index page.
document.addEventListener('DOMContentLoaded', () => {
    mainPage.Instance.show();
});
